import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Carregando from "../componentes/Carregando";
import {
  Alerta,
  Bloco,
  LegendaDeIntegradores,
  ListaDeEstados,
  MapaDoBrasil,
  PontoDeIntegrador,
} from "../componentes/LegalizaUi";
import LegalizaRedator from "./LegalizaRedator";
import {
  AVISOS,
  RESPONSABILIDADE,
  acharModelo,
  baixarArquivo,
  lerCatalogo,
  manuaisDoDrei,
  modelosDaCasa,
  modelosDaJunta,
  nomeParaDownload,
  porCategoria,
  rotuloDoIntegrador,
  type Catalogo,
  type EstadoDaLegalizacao,
  type Integrador,
  type ModeloDeContrato,
} from "../lib/legaliza";

// O Legaliza Fácil: o mapa das 27 juntas comerciais, os sistemas oficiais de cada
// uma e os 68 modelos de contrato.
//
// ---------------------------------------------------------------------------
// De onde isto veio
// ---------------------------------------------------------------------------
// Portado do app externo `legalizafacil.contabilidadefacilitada.com` (Lovable,
// React 18 + Vite), a pedido de Victor em 25/08 — a QUINTA e última satélite da
// `000064` a vir para dentro. O que mudou no caminho está em
// `docs/runbooks/legaliza-facil.md`; em resumo:
//
//   · os 27 estados e as 81 URLs saíram de uma constante de 366 linhas para
//     `legaliza_estados`, com tela de admin (000115) — o endereço do Sergipe já
//     estava velho no código e só funcionava por redirect;
//   · os 66 arquivos saíram de `public/` para um bucket privado cuja policy
//     pergunta pelo card;
//   · os dois PDFs da JUCEES que estavam no disco e a tela nunca mostrou passaram
//     a aparecer — o Espírito Santo dizia que fornecia modelos e não fornecia;
//   · modelo da junta e modelo da casa viraram DUAS listas com procedência no
//     título, em vez de uma lista misturada com um selo "CF" de 9px;
//   · o editor exporta PDF por impressão, e o `.docx` que se baixa é o modelo
//     ORIGINAL — ver o cabeçalho de `LegalizaRedator.tsx`.
//
// ---------------------------------------------------------------------------
// Os segmentos, e o que é RESERVADO
// ---------------------------------------------------------------------------
//   /ferramenta/legaliza-facil            o mapa
//   /ferramenta/legaliza-facil/SP         o estado
//   /ferramenta/legaliza-facil/SP/modelo/<id>   o modelo aberto para preencher
//
// O primeiro segmento é uma SIGLA de estado, e `modelo` é a única palavra
// reservada depois dela. Como no MEI PRO e no Simulador, se um dia esta
// ferramenta ganhar conteúdo pelo motor da 000073, uma seção chamada `modelo`
// ficaria inalcançável — e nenhuma seção pode se chamar "SP".
//
// Estado EFÊMERO: nada do que o aluno digita no editor vai para o banco. É o
// corte da 000098, e o rascunho fica no `localStorage` do próprio navegador, como
// o checklist de admissão do HUB DP — preencher um contrato social leva tempo, e
// perder tudo ao fechar a aba tornaria o editor inútil.

export default function LegalizaFacil() {
  const { "*": resto = "" } = useParams();
  const segmentos = resto.split("/").filter(Boolean);
  const uf = segmentos[0]?.toUpperCase() ?? null;
  const modeloId = segmentos[1] === "modelo" ? (segmentos[2] ?? null) : null;

  const [catalogo, setCatalogo] = useState<Catalogo | null>(null);
  const [fase, setFase] = useState<"carregando" | "pronto" | "erro">("carregando");

  useEffect(() => {
    let vivo = true;
    lerCatalogo().then((c) => {
      if (!vivo) return;
      setCatalogo(c);
      setFase(c ? "pronto" : "erro");
    });
    return () => {
      vivo = false;
    };
  }, []);

  if (fase === "carregando") return <Carregando />;
  if (fase === "erro" || !catalogo) {
    return (
      <Vazio titulo="Não conseguimos carregar o mapa da legalização">
        Tente de novo em instantes. Se continuar, avise o suporte.
      </Vazio>
    );
  }

  const estado = uf ? (catalogo.estados.find((e) => e.uf === uf) ?? null) : null;

  if (uf && !estado) {
    return (
      <Vazio titulo={`Não existe estado com a sigla "${uf}"`}>
        Escolha um dos 27 na lista abaixo.
        <div className="mt-6 text-left">
          <ListaDeEstados estados={catalogo.estados} base={BASE} />
        </div>
      </Vazio>
    );
  }

  if (estado && modeloId) {
    const modelo = acharModelo(catalogo, modeloId);
    if (!modelo) {
      return (
        <Vazio titulo="Este modelo não está mais disponível">
          Ele pode ter sido substituído. Veja os modelos de {estado.nome} na página do estado.
          <div className="mt-6">
            <Link to={`${BASE}/${estado.uf}`} className="pill-cta inline-flex">
              Voltar para {estado.nome}
            </Link>
          </div>
        </Vazio>
      );
    }
    return <LegalizaRedator estado={estado} modelo={modelo} />;
  }

  if (estado) return <TelaDoEstado catalogo={catalogo} estado={estado} />;
  return <TelaDoMapa catalogo={catalogo} />;
}

const BASE = "/ferramenta/legaliza-facil";

// ---------------------------------------------------------------------------
// A entrada: o mapa
// ---------------------------------------------------------------------------

function TelaDoMapa({ catalogo }: { catalogo: Catalogo }) {
  const navegar = useNavigate();
  const [sobre, setSobre] = useState<string | null>(null);

  const contagem = useMemo(() => {
    const c = {} as Record<Integrador, number>;
    for (const e of catalogo.estados) c[e.integrador] = (c[e.integrador] ?? 0) + 1;
    return c;
  }, [catalogo]);

  const comModeloProprio = useMemo(
    () => new Set(catalogo.modelos.filter((m) => m.uf).map((m) => m.uf as string)),
    [catalogo],
  );

  return (
    <main className="mx-auto max-w-6xl px-4 py-7 sm:px-6">
      <section className="painel-navy rounded-cf-lg p-6 sm:p-8">
        <div className="relative">
          <p className="font-display text-xs font-bold uppercase tracking-widest text-laranja-claro">
            Legaliza Fácil
          </p>
          <h1 className="mt-2 font-display text-3xl font-bold text-white sm:text-4xl">
            Onde a empresa vai ser registrada?
          </h1>
          <p className="mt-2 max-w-2xl text-navy-mute">
            Cada estado tem a sua junta comercial e o seu sistema. Escolha o estado para ver os
            endereços oficiais de abertura, alteração e baixa — e os modelos de contrato que
            servem lá.
          </p>

          <div className="mt-7 grid items-start gap-6 lg:grid-cols-[1fr_15rem]">
            {/* `max-w` no mapa e altura pela razão do desenho: sem o limite, com
                800 px de largura ele passava de 840 px de altura e a entrada
                virava uma tela e meia só de painel. */}
            <div className="mx-auto w-full max-w-[34rem]">
              <MapaDoBrasil
                estados={catalogo.estados}
                ativo={sobre}
                onEscolher={(uf) => navegar(`${BASE}/${uf}`)}
              />
            </div>
            <div className="lg:pt-2">
              <h2 className="font-display text-xs font-bold uppercase tracking-widest text-navy-mute">
                Os quatro sistemas
              </h2>
              <div className="mt-3">
                <LegendaDeIntegradores contagem={contagem} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section role="group" aria-labelledby="lista-de-estados" className="surface mt-6 p-5 sm:p-6">
        <h2 id="lista-de-estados" className="font-display text-lg font-bold text-titulo">
          Os 27 estados
        </h2>
        <p className="mt-1 text-sm text-cinza">
          {comModeloProprio.size} juntas publicam modelo próprio. Nas outras, os modelos são os da
          Contabilidade Facilitada — e eles aparecem em todos os estados.
        </p>
        <div className="mt-4" onMouseLeave={() => setSobre(null)}>
          <ListaDeEstados estados={catalogo.estados} base={BASE} />
        </div>
      </section>
    </main>
  );
}

// ---------------------------------------------------------------------------
// A tela do estado
// ---------------------------------------------------------------------------

function TelaDoEstado({ catalogo, estado }: { catalogo: Catalogo; estado: EstadoDaLegalizacao }) {
  const daJunta = modelosDaJunta(catalogo, estado.uf);
  const daCasa = modelosDaCasa(catalogo);
  const manuais = manuaisDoDrei(catalogo);

  const sistemas = [
    { rotulo: "Abrir empresa / desenquadrar MEI", url: estado.link_abertura },
    { rotulo: "Alterar empresa", url: estado.link_alteracao },
    { rotulo: "Baixar empresa", url: estado.link_baixa },
  ];
  // Nove estados usam o mesmo endereço para os três atos (o portal único da Rede
  // Simples), e em cinco a alteração e a baixa vão para o coletor nacional da
  // Receita. Dizer isso é útil: o contador que clica em "Alterar" e cai no mesmo
  // lugar de "Abrir" acha que a tela está quebrada.
  const portalUnico =
    estado.link_abertura === estado.link_alteracao &&
    estado.link_alteracao === estado.link_baixa;
  const viaReceita =
    !portalUnico && estado.link_alteracao.includes("redesimservicos.rfb.gov.br");

  return (
    <main className="mx-auto max-w-5xl px-4 py-7 sm:px-6">
      <Link
        to={BASE}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-link hover:underline"
      >
        <span aria-hidden="true">‹</span> Voltar para o mapa
      </Link>

      <header className="painel-navy mt-4 rounded-cf-lg p-6 sm:p-8">
        <div className="relative">
          {/* O ponto repete a cor que o aluno acabou de clicar no mapa — é o que
              liga esta página ao território de onde ele veio. Dentro do navy a
              cor certa vem sozinha: o território redefine a variável. */}
          <p className="flex items-center gap-2 font-display text-xs font-bold uppercase tracking-widest text-laranja-claro">
            <PontoDeIntegrador integrador={estado.integrador} />
            {rotuloDoIntegrador(estado.integrador)}
          </p>
          <h1 className="mt-2 font-display text-3xl font-bold text-white">{estado.nome}</h1>
          <p className="mt-1 text-navy-mute">
            Junta Comercial: <span className="font-semibold text-white">{estado.junta}</span>
          </p>
        </div>
      </header>

      <div className="mt-6 space-y-6">
        <Bloco
          titulo="Sistemas oficiais"
          descricao={
            portalUnico
              ? "Neste estado os três atos começam no mesmo portal da junta — é assim mesmo."
              : viaReceita
                ? "A abertura começa na junta; alteração e baixa vão pelo coletor nacional da Receita Federal."
                : "Um endereço para cada ato, no portal do estado."
          }
        >
          <div className="grid gap-3 sm:grid-cols-3">
            {sistemas.map((s) => (
              <a
                key={s.rotulo}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className="group flex items-start justify-between gap-2 rounded-cf border border-borda bg-superficie-2 p-4 transition hover:border-laranja"
              >
                <span className="text-sm font-semibold text-tinta group-hover:text-laranja-texto">
                  {s.rotulo}
                </span>
                <span aria-hidden="true" className="shrink-0 text-cinza">
                  ↗
                </span>
                <span className="sr-only">(abre em outra aba)</span>
              </a>
            ))}
          </div>
        </Bloco>

        <Bloco
          titulo="Antes de redigir"
          descricao="Dois avisos que valem para qualquer estado."
        >
          <ul className="space-y-2.5">
            {AVISOS.map((a) => (
              <li key={a} className="flex gap-2 text-sm leading-relaxed text-tinta">
                <span aria-hidden="true" className="text-cinza">
                  •
                </span>
                {a}
              </li>
            ))}
          </ul>
        </Bloco>

        {/*
          A ORDEM aqui é deliberada: os modelos da própria junta primeiro, porque
          são os que aquela junta aceita sem discussão. Depois os da casa, que
          servem em qualquer estado. E o bloco da junta APARECE mesmo quando ela
          não publica nada — dizendo isso com letra.

          No app de origem esse caso era ausência: os modelos da casa entravam na
          mesma lista e o aluno não tinha como saber se a junta publicava algo.
          É a cicatriz da linha condicional que some da aba Prazos: quando a
          ausência carrega informação, ela precisa de texto próprio.
        */}
        <Bloco
          titulo={`Modelos da ${estado.junta}`}
          descricao={
            daJunta.length > 0
              ? `Publicados pela própria junta de ${estado.nome}.`
              : undefined
          }
        >
          {daJunta.length > 0 ? (
            <ListaDeModelos modelos={daJunta} uf={estado.uf} />
          ) : (
            <p className="text-sm leading-relaxed text-cinza">
              A {estado.junta} não publica modelo de contrato próprio. Use os modelos da
              Contabilidade Facilitada abaixo, adaptando ao caso —{" "}
              {estado.nota_de_origem?.toUpperCase() === "MANUAL"
                ? "a junta publica um manual de orientação, que vale consultar no site oficial."
                : "eles valem para qualquer estado."}
            </p>
          )}
        </Bloco>

        <Bloco
          titulo="Modelos da Contabilidade Facilitada"
          descricao="Genéricos, para qualquer estado. Adapte as cláusulas ao caso."
        >
          <ListaDeModelos modelos={daCasa} uf={estado.uf} />
        </Bloco>

        <Bloco
          titulo="Manuais de registro"
          descricao="Oficiais do DREI, aplicáveis a qualquer junta comercial."
        >
          <ListaDeModelos modelos={manuais} uf={estado.uf} />
        </Bloco>

        <Alerta>{RESPONSABILIDADE}</Alerta>
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// A lista de modelos
// ---------------------------------------------------------------------------

function ListaDeModelos({ modelos, uf }: { modelos: ModeloDeContrato[]; uf: string }) {
  const grupos = porCategoria(modelos);
  return (
    <div className="space-y-5">
      {grupos.map((g) => (
        <div key={g.categoria}>
          {/* O rótulo de categoria some quando só há um grupo: "MANUAL" sozinho
              acima de dois manuais não informa nada, e é ruído numa tela que já
              tem cinco blocos. */}
          {grupos.length > 1 && (
            <h4 className="mb-2 font-display text-xs font-bold uppercase tracking-widest text-cinza">
              {g.rotulo}
            </h4>
          )}
          <ul className="grid gap-2 sm:grid-cols-2">
            {g.itens.map((m) => (
              <li key={m.id}>
                <LinhaDeModelo modelo={m} uf={uf} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function LinhaDeModelo({ modelo, uf }: { modelo: ModeloDeContrato; uf: string }) {
  const [baixando, setBaixando] = useState(false);
  const [erro, setErro] = useState(false);

  async function baixar() {
    setBaixando(true);
    setErro(false);
    const blob = await baixarArquivo(modelo.arquivo);
    setBaixando(false);
    if (!blob) {
      setErro(true);
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = nomeParaDownload(modelo.titulo, modelo.formato, modelo.uf);
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  return (
    <div className="flex items-stretch overflow-hidden rounded-cf border border-borda bg-superficie-2">
      <button
        type="button"
        onClick={baixar}
        disabled={baixando}
        className="flex min-w-0 flex-1 items-center gap-2.5 p-3 text-left transition hover:bg-tile disabled:opacity-60"
      >
        <span aria-hidden="true" className="shrink-0 text-cinza">
          ↓
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-tinta">{modelo.titulo}</span>
          {/* Erro de UMA escrita não apaga a tela: a linha que falhou é que
              informa. É a lição do `alternarFeito` do Calendário, onde um tique
              que não gravou trocava o mês inteiro por uma mensagem de erro. */}
          {erro && (
            <span className="mt-0.5 block text-xs text-erro-tinta">
              Não conseguimos baixar agora. Tente de novo.
            </span>
          )}
        </span>
        <span className="shrink-0 rounded bg-chip px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-chip-tinta">
          {baixando ? "…" : modelo.formato}
        </span>
      </button>

      {/* Só `.docx` abre no editor: um PDF de manual de 6 MB não é um documento
          para preencher, e oferecer "Preencher" nele seria prometer o que a tela
          não faz. */}
      {modelo.formato === "docx" && (
        <Link
          to={`${BASE}/${uf}/modelo/${modelo.id}`}
          className="flex shrink-0 items-center border-l border-borda px-3 text-xs font-semibold text-cinza transition hover:bg-laranja hover:text-azul"
        >
          Preencher
        </Link>
      )}
    </div>
  );
}

function Vazio({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <main className="mx-auto max-w-3xl px-4 py-14 text-center sm:px-6">
      <h1 className="font-display text-2xl font-bold text-titulo">{titulo}</h1>
      <div className="mt-2 text-cinza">{children}</div>
    </main>
  );
}
